import type { ReactElement } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { GITLEAKS_DEFAULT_RULE_IDS } from '@kirin/api-client/data/gitleaks-rules';
import { Button } from '@/components/ui/button';
import {
  SECRET_DETECTION_GITLEAKS_RULES_SLUG,
  policySubPath,
  withPolicyScope,
} from '@/lib/policyPaths';

interface GitleaksRulesLinkProps {
  /** Rule IDs currently turned off, used for the "N off" summary. */
  disabledCount: number;
  /** Read-only viewers get a "View" affordance instead of "Edit". */
  readOnly?: boolean;
}

/**
 * KIR-4590: the "Secret Detection Rules" row on the Secret Detection policy
 * card.
 * Replaces the raw `disabledGitleaksRules` chip list with a link to the full
 * editor, which the alert drilldown also deep-links into.
 *
 * The label count is the TOTAL ruleset size, not the number turned off.
 */
export function GitleaksRulesLink({
  disabledCount,
  readOnly = false,
}: GitleaksRulesLinkProps): ReactElement {
  const { organizationId } = useParams<{ organizationId?: string }>();
  const { search } = useLocation();

  // The editor reads and writes through the Policies tab's scope-derived hooks,
  // so the security-group scope has to travel with the link.
  const rulesPath = withPolicyScope(
    policySubPath(organizationId, SECRET_DETECTION_GITLEAKS_RULES_SLUG),
    search
  );

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">
        The {GITLEAKS_DEFAULT_RULE_IDS.length} built-in secret detection rules,
        run on the device when scanning chat content. Turn off rules that are
        noisy for your organization. Kirin custom secret rules and secret file
        rules are managed separately and are unaffected.
        {disabledCount > 0 ? ` ${disabledCount} currently turned off.` : ''}
      </p>
      <Button asChild variant="outline">
        <Link to={rulesPath}>
          {readOnly ? 'View' : 'Edit'} Secret Detection Rules (
          {GITLEAKS_DEFAULT_RULE_IDS.length})
        </Link>
      </Button>
    </div>
  );
}
